<template>
    <section class="flex flex-col flex-1">
        <div class="flex-1 container mx-auto">
            <div class="w-full max-w-2xl mx-auto" v-if="results && results.length === 0">
                <div class="px-6">
                    <h1 class="text-3xl font-bold test-gray-700">Need to find your building?</h1>
                    <p class="text-gray-700 text-lg font-semibold mt-2">
                        Find your building ID, gross floor area, use type, current compliance status, and Building Energy Performance Standards targets and baseline.
                    </p>
                </div>
                <div class="mt-4">
                    <card-container spacing="lg" class="w-full">
                        <tabs-menu class="mb-4" :tabs="tabs" :selected-tab="selectedTab" @change="handleChangeTab" />
                        <form id="panel_1" @submit.prevent="search" v-show="selectedTab === 0">
                            <view-errors :error="error" />
                            <div>
                                <form-group>
                                    <form-label for="street" is-required>Street</form-label>
                                    <text-input id="street" class="block w-full" v-model="address.street" />
                                </form-group>
                                <div class="mt-4">
                                    <loading-button type="submit" :loading="loading" :disabled="!address.street">
                                        <search-icon class="h-4 w-4 mr-2" />
                                        Search Address
                                    </loading-button>
                                </div>
                            </div>
                        </form>
                        <form id="panel_2" @submit.prevent="searchById" v-show="selectedTab === 1">
                            <view-errors :error="error" />
                            <div>
                                <form-group>
                                    <form-label for="building_id" is-required>Building ID</form-label>
                                    <text-input id="building_id" class="block w-full" v-model="building.building_id" />
                                </form-group>
                                <div class="mt-4">
                                    <loading-button type="submit" :loading="loading" :disabled="!building.building_id">
                                        <search-icon class="h-4 w-4 mr-2" />
                                        Search Building ID
                                    </loading-button>
                                </div>
                            </div>
                        </form>
                    </card-container>
                </div>
            </div>
            <div v-else>
                <div class="flex flex-col lg:flex-row lg:items-center">
                    <div class="flex-1 mr-2">
                        <h1 class="text-2xl font-bold test-gray-700">Buildings matching search criteria</h1>
                        <p class="text-gray-700 font-semibold mt-2">
                            Don’t see your building listed? Try variations of your building’s address or email the <a href="mailto:energy@montgomerycountymd.gov" class="text-blue underline">Montgomery County Benchmarking Helpdesk</a> or call us at 240-777-7707 for assistance.
                        </p>
                    </div>
                    <div class="mt-4 lg:mt-0">
                        <base-button @click="handleClearResults" color="blue">
                            <chevron-left-icon class="h-5 w-5 mr-2" />
                            New Search
                        </base-button>
                    </div>
                </div>
                <card-container spacing="lg" class="w-full mt-4 overflow-hidden">
                    <div class="-mx-6 -my-4">
                        <div class="grid grid-cols-1">
                            <base-table class="report-table" :data="results" :columns="columns" :loading="loading" :current-page="paging.currentPage" :per-page="paging.perPage" paginated>
                                <template v-slot="{ row, index }">
                                    <td :class="`${ tableRowBaseClass(index) }`">{{ row.custom_building_id }}</td>
                                    <td :class="`${ tableRowBaseClass(index) } capitalize`">{{ row.usetype | empty_string_format }}</td>
                                    <td :class="`${ tableRowBaseClass(index) }`">{{ row.gross_floor_area | number_format }}</td>
                                    <td :class="`${ tableRowBaseClass(index) }`">{{ row.gfa_reported | number_format }}</td>
                                    <td :class="`${ tableRowBaseClass(index) }`">{{ row.status | capitalize_words_format }}</td>
                                    <td :class="`${ tableRowBaseClass(index) }`">{{ row.street | capitalize_words_format }}</td>
                                    <td :class="`${ tableRowBaseClass(index) }`">{{ row.city | capitalize_words_format }}</td>
                                    <td :class="`${ tableRowBaseClass(index) }`">{{ row.zipcode| empty_string_format }}</td>
                                    <td :class="`${ tableRowBaseClass(index) }`">
                                        <router-link
                                            :to="formattedRowLink(row)"
                                            class="
                                                cursor-pointer
                                                rounded
                                                text-blue
                                                hover:text-blue-700
                                                focus:outline-none
                                                focus:ring-2
                                                focus:ring-offset-2
                                                focus:ring-blue
                                                focus:ring-opacity-25
                                                transition
                                                duration-300
                                                ease-in-out
                                            "
                                            @click="$emit('click')"
                                        >
                                            View
                                        </router-link>
                                    </td>
                                </template>
                            </base-table>
                        </div>
                    </div>
                </card-container>
            </div>
        </div>
    </section>
</template>
<script>
import { SearchIcon, ChevronLeftIcon, ViewErrorsMixin } from '@touchstone/touchstone-assets';
import ApiService from '@/services/apiService';
import { RouterLink } from 'vue-router';
// @ is an alias to /src
export default {
    name: 'SearchBuilding',
    mixins: [ViewErrorsMixin],
    components: {
        RouterLink,
        ChevronLeftIcon,
        SearchIcon,
    },
    data() {
        const year = (new Date()).getFullYear();
        return {
            address: {
                street: '',
            },
            building: {
                building_id: ''
            },
            columns: [
                { label: 'Montgomery County Building ID', field: 'custom_building_id' },
                { label: 'Use Type', field: 'usetype' },
                { label: 'Assessor GFA (sq-ft)', field: 'gross_floor_area' },
                { label: 'Reported GFA (sq-ft)', field: 'gfa_reported' },
                { label: `${year} Status`, field: 'status' },
                { label: 'Street', field: 'street' },
                { label: 'City', field: 'city' },
                { label: 'Postal Code', field: 'zipcode' },
                { label: '', field: '' }
            ],
            tabs: [
                'Search by Address', 'Search by Building ID'
            ],
            selectedTab: 0,
            paging: {
                currentPage: 1,
                perPage: 10
            },
            loading: false,
            results: []
        };
    },
    methods: {
        formattedRowLink(row) {
            return `/details/${row.custom_building_id}`;
        },
        tableRowBaseClass(index) {
            return index % 2 ? 'bg-blue-50' : 'bg-blue-100';
        },
        async search() {
            try {
                this.loading = true;
                this.clearError();
                const payload = {
                    street: this.address.street,
                };
                const { data } = await this.api.searchBuilding(payload);
                if (data && data.length === 0) {
                    this.$touchstone.notify({
                        color: 'blue',
                        message: 'No matching results were found.',
                        position: 'top-right'
                    });
                }
                this.results = data;
            } catch (e) {
                this.setError(e);
            }
            this.loading = false;
        },
        async searchById() {
            try {
                this.loading = true;
                this.clearError();
                const { data } = await this.api.searchBuildingById(this.building);
                if (data && data.length === 0) {
                    this.$touchstone.notify({
                        color: 'blue',
                        message: 'No matching results were found.',
                        position: 'top-right'
                    });
                }
                this.results = data;
            } catch (e) {
                this.setError(e);
            }
            this.loading = false;
        },
        handleClearResults() {
            this.results = [];
        },
        handleChangeTab(index) {
            this.selectedTab = index;
        },
    },
    created() {
        const srv = new ApiService();
        this.api = srv.searchApi();
    }
};
</script>
